import { Box, Button, Container, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import sdPlane from '../../assets/home/hero/sd_plane.webp';
import ocean from '../../assets/home/hero/Ocean.webp';
import bay from '../../assets/home/hero/bay.webp';
import japaneseGarden from '../../assets/home/hero/japanese_garden.webp';

const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroImages = [ocean, japaneseGarden, bay, sdPlane];
  const totalSlides = heroImages.length;
  
  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 8000); // Change slide every 8 seconds
    
    return () => clearInterval(interval);
  }, [totalSlides, currentSlide]);
  
  // Navigation functions
  const goToNextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);
  
  const goToPrevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);
  
  // Function to go to a specific slide and reset timer
  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        height: { 
          xs: '72vh',
          md: '80vh',
          lg: '85vh',
          xl: '90vh'
        },
        minHeight: '480px',
        maxHeight: {
          xs: '600px',
          md: '700px',
          lg: '800px',
          xl: '900px'
        },
        width: {
          xs: '100%',
          md: 'calc(100% - 64px)',  // Accounting for margins
          lg: 'calc(100% - 96px)',
          xl: 'calc(100% - 128px)'
        },
        mx: {
          xs: 0,
          md: 'auto'  // Center the container
        },
        overflow: 'hidden',
        borderRadius: {
          xs: 0,
          md: '12px'
        },
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        WebkitPerspective: 1000,
        perspective: 1000,
        willChange: 'transform',
        '@media (hover: hover)': {
          '&:hover .carousel-nav': {
            opacity: 1,
          }
        },
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 }
        },
      }}
    >
      {/* Image carousel */}
      {heroImages.map((image, index) => (
        <Box
          key={index}
          id={`slide-${index}`}
          role="tabpanel"
          aria-hidden={currentSlide !== index}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: currentSlide === index ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
            zIndex: currentSlide === index ? 0 : -1,
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            transform: 'translateZ(0)',
            willChange: 'opacity',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'brightness(0.7)',
              WebkitBackfaceVisibility: 'hidden',
              backfaceVisibility: 'hidden',
              transform: 'translateZ(0)',
              willChange: 'filter',
              animation: currentSlide === index ? 'fadeIn 1s ease-in-out' : 'none',
            },
          }}
        />
      ))}

      {/* Navigation Arrows */}
      <Button
        className="carousel-nav"
        onClick={goToPrevSlide}
        aria-label="Previous slide"
        sx={{
          position: 'absolute',
          left: 20,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          minWidth: 50,
          width: 50,
          height: 50,
          borderRadius: '50%',
          bgcolor: 'rgba(255, 255, 255, 0.7)',
          color: 'text.primary',
          fontWeight: 'bold',
          fontSize: '1.5rem',
          opacity: { xs: 1, md: 0 },
          transition: 'opacity 0.3s, background-color 0.3s',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.9)',
          },
          '&:focus-visible': {
            outline: '2px solid white',
            outlineOffset: 2,
            opacity: 1
          }
        }}
      >
        ←
      </Button>

      <Button
        className="carousel-nav"
        onClick={goToNextSlide}
        aria-label="Next slide"
        sx={{
          position: 'absolute',
          right: 20,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          minWidth: 50,
          width: 50,
          height: 50,
          borderRadius: '50%',
          bgcolor: 'rgba(255, 255, 255, 0.7)',
          color: 'text.primary',
          fontWeight: 'bold',
          fontSize: '1.5rem',
          opacity: { xs: 1, md: 0 },
          transition: 'opacity 0.3s, background-color 0.3s',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.9)',
          },
          '&:focus-visible': {
            outline: '2px solid white',
            outlineOffset: 2,
            opacity: 1
          }
        }}
      >
        →
      </Button>

      {/* Indicator dots */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1,
          zIndex: 2
        }}
        role="tablist"
        aria-label="Carousel navigation"
      >
        {heroImages.map((_, index) => (
          <Box
            key={index}
            onClick={() => goToSlide(index)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToSlide(index);
              }
            }}
            role="tab"
            tabIndex={0}
            aria-selected={currentSlide === index}
            aria-label={`Slide ${index + 1}`}
            aria-controls={`slide-${index}`}
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: currentSlide === index ? 'white' : 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              transition: 'background-color 0.3s',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.8)',
              },
              '&:focus-visible': {
                outline: '2px solid white',
                outlineOffset: 2
              }
            }}
          />
        ))}
      </Box>

      {/* Content overlay */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          zIndex: 1,
          py: 4,
        }}
      >
        <Box
          sx={{
            px: 4,
            py: 5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.3) 100%)',
            backdropFilter: 'blur(3px)',
            maxWidth: { xs: '95%', sm: '80%', md: '70%' },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}
        >
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            fontWeight="bold"
            sx={{
              color: 'white !important',
              background: 'none !important',
              WebkitBackgroundClip: 'initial !important',
              WebkitTextFillColor: 'initial !important',
              backgroundClip: 'initial !important',
              textShadow: '2px 2px 8px rgba(0,0,0,0.8), 0 0 1px rgba(0,0,0,0.6)',
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.75rem' },
            }}
          >
            Freedom in the Outdoors
          </Typography>
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom 
            sx={{ 
              mb: 3,
              color: 'white',
              textShadow: '2px 2px 8px rgba(0,0,0,0.8), 0 0 1px rgba(0,0,0,0.6)',
              fontSize: { xs: '1.25rem', md: '1.5rem' },
            }}
          >
            Gear to play<br />
            Gear to prevent<br />
            Gear to perservere
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            component={RouterLink}
            to="/products"
            sx={{ 
              fontWeight: 'bold', 
              px: 4, 
              py: 1.5,
              backdropFilter: 'blur(4px)',
              '@media (hover: hover)': {
                '&:hover': {
                  transform: 'scale(1.05) translateZ(0)',
                  transition: 'transform 0.2s ease-out',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                },
              },
            }}
          >
            Shop our Gear
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroCarousel; 